import {
    ActionIcon,
    Badge,
    Button,
    Group,
    Loader,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import { useEffect, useMemo, useState, type FC } from 'react';

import type { DestFillPolicy, RebalanceNode, SourceSplitPolicy, UpsertRebalanceNode } from '@api/rebalance';
import { getRebalanceNodes, upsertRebalanceNodes } from '@api/rebalance';
import { showError, showSuccess } from '@utils/notifications';
import styles from './RebalanceNodesTab.module.css';

/** Local, editable shape. `key` keeps React stable for nodes that have no server id yet. */
interface DraftMember {
    warehouseId: string;
    warehouseName: string | null;
    keepFloorDoh: number | '';
}
interface DraftNode {
    key: string;
    id?: string;
    name: string;
    targetDoh: number | '';
    sourceSplitPolicy: SourceSplitPolicy | 'DEFAULT';
    destFillPolicy: DestFillPolicy | 'DEFAULT';
    /** Ordered: index = rank − 1, used for both send and receive order. */
    members: DraftMember[];
}

const apiErrorMessage = (err: unknown, fallback: string): string => {
    const e = err as { error?: { message?: string | string[] }; message?: string } | null;
    const raw = e?.error?.message ?? e?.message;
    const text = Array.isArray(raw) ? raw.join('; ') : raw;
    return text && text.trim() ? text : fallback;
};

const toDraft = (n: RebalanceNode): DraftNode => ({
    key: n.id,
    id: n.id,
    name: n.name,
    targetDoh: n.targetDoh ?? '',
    sourceSplitPolicy: n.sourceSplitPolicy ?? 'DEFAULT',
    destFillPolicy: n.destFillPolicy ?? 'DEFAULT',
    members: [...n.members]
        .sort((a, b) => a.sourceRank - b.sourceRank)
        .map(m => ({ warehouseId: m.warehouseId, warehouseName: m.warehouseName, keepFloorDoh: m.keepFloorDoh ?? '' })),
});

const toPayload = (d: DraftNode): UpsertRebalanceNode => ({
    id: d.id,
    name: d.name.trim(),
    targetDoh: d.targetDoh === '' ? null : Number(d.targetDoh),
    sourceSplitPolicy: d.sourceSplitPolicy === 'DEFAULT' ? null : d.sourceSplitPolicy,
    destFillPolicy: d.destFillPolicy === 'DEFAULT' ? null : d.destFillPolicy,
    isActive: true,
    members: d.members.map((m, i) => ({
        warehouseId: m.warehouseId,
        sourceRank: i + 1,
        destRank: i + 1,
        keepFloorDoh: m.keepFloorDoh === '' ? null : Number(m.keepFloorDoh),
    })),
});

const RebalanceNodesTab: FC = () => {
    const [nodes, setNodes] = useState<DraftNode[]>([]);
    const [saved, setSaved] = useState<string>('');
    const [allWarehouses, setAllWarehouses] = useState<{ warehouseId: string; name: string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pickerFor, setPickerFor] = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        getRebalanceNodes()
            .then(r => {
                const drafts = r.nodes.map(toDraft);
                setNodes(drafts);
                setSaved(JSON.stringify(drafts.map(toPayload)));
                const known = new Map<string, string>();
                r.unassignedWarehouses.forEach(w => known.set(w.warehouseId, w.name));
                r.nodes.forEach(n => n.members.forEach(m => known.set(m.warehouseId, m.warehouseName ?? m.warehouseId)));
                setAllWarehouses([...known.entries()].map(([warehouseId, name]) => ({ warehouseId, name })).sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch(() => setError('Could not load nodes.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const assigned = useMemo(() => {
        const map = new Map<string, string>();
        nodes.forEach(n => n.members.forEach(m => map.set(m.warehouseId, n.name || 'Unnamed node')));
        return map;
    }, [nodes]);

    const unassigned = allWarehouses.filter(w => !assigned.has(w.warehouseId));
    const isDirty = JSON.stringify(nodes.map(toPayload)) !== saved;

    const update = (key: string, patch: Partial<DraftNode>) =>
        setNodes(prev => prev.map(n => (n.key === key ? { ...n, ...patch } : n)));

    const addNode = () =>
        setNodes(prev => [...prev, { key: `new-${Date.now()}`, name: '', targetDoh: '', sourceSplitPolicy: 'DEFAULT', destFillPolicy: 'DEFAULT', members: [] }]);

    const removeNode = (node: DraftNode) => {
        modals.openConfirmModal({
            title: 'Remove node?',
            children: (
                <Text size="sm">
                    <strong>{node.name || 'This node'}</strong> will be removed when you save. Its warehouses go back to standing alone in network runs. Routes are not affected.
                </Text>
            ),
            labels: { confirm: 'Remove', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: () => setNodes(prev => prev.filter(n => n.key !== node.key)),
        });
    };

    const addMember = (key: string, warehouseId: string) => {
        const wh = allWarehouses.find(w => w.warehouseId === warehouseId);
        if (!wh) return;
        update(key, { members: [...(nodes.find(n => n.key === key)?.members ?? []), { warehouseId, warehouseName: wh.name, keepFloorDoh: '' }] });
        setPickerFor(null);
    };

    const moveMember = (key: string, index: number, dir: -1 | 1) => {
        const node = nodes.find(n => n.key === key);
        if (!node) return;
        const next = [...node.members];
        const target = index + dir;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        update(key, { members: next });
    };

    const save = async () => {
        if (nodes.some(n => !n.name.trim())) { setError('Every node needs a name.'); return; }
        setSaving(true);
        setError(null);
        try {
            const r = await upsertRebalanceNodes(nodes.map(toPayload));
            const drafts = r.nodes.map(toDraft);
            setNodes(drafts);
            setSaved(JSON.stringify(drafts.map(toPayload)));
            showSuccess('Nodes saved');
        } catch (err) {
            const msg = apiErrorMessage(err, 'Save failed. Please try again.');
            setError(msg);
            showError(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Loader size="sm" />;

    return (
        <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
                <div>
                    <Title order={6}>Geo nodes</Title>
                    <Text size="xs" c="dimmed" maw={640}>
                        A node is a cluster of warehouses the network run treats as one stock position — "is Mumbai covered for 30 days?".
                        Order inside a node is the send and receive priority. A warehouse belongs to one node; warehouses in no node stand alone.
                    </Text>
                </div>
                <Group gap="xs">
                    <Button size="compact-sm" variant="light" leftSection={<IconPlus size={14} />} onClick={addNode}>Add node</Button>
                    {isDirty && <Button size="compact-sm" color="violet" loading={saving} onClick={save}>Save nodes</Button>}
                </Group>
            </Group>
            {error && <Text size="xs" c="red">{error}</Text>}

            {nodes.length === 0 && (
                <Text size="sm" c="dimmed">No nodes yet. Add one, give it a name, and pick its warehouses.</Text>
            )}

            {nodes.map(node => (
                <div key={node.key} className={styles.nodeCard}>
                    <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
                        <Group gap="sm" align="flex-end" wrap="wrap">
                            <TextInput
                                label="Node name"
                                size="xs"
                                w={200}
                                placeholder="e.g. Delhi"
                                value={node.name}
                                onChange={e => update(node.key, { name: e.currentTarget.value })}
                            />
                            <NumberInput
                                label="Target DoH"
                                size="xs"
                                w={120}
                                placeholder="org default"
                                min={1}
                                suffix="d"
                                value={node.targetDoh}
                                onChange={v => update(node.key, { targetDoh: v === '' ? '' : Number(v) })}
                            />
                            <div>
                                <Text size="xs" fw={500} mb={4}>Send from</Text>
                                <SegmentedControl
                                    size="xs"
                                    value={node.sourceSplitPolicy}
                                    onChange={v => update(node.key, { sourceSplitPolicy: v as DraftNode['sourceSplitPolicy'] })}
                                    data={[
                                        { label: 'Default', value: 'DEFAULT' },
                                        { label: 'By rank', value: 'RANK' },
                                        { label: 'By availability', value: 'AVAILABILITY' },
                                    ]}
                                />
                            </div>
                            <div>
                                <Text size="xs" fw={500} mb={4}>Receive into</Text>
                                <SegmentedControl
                                    size="xs"
                                    value={node.destFillPolicy}
                                    onChange={v => update(node.key, { destFillPolicy: v as DraftNode['destFillPolicy'] })}
                                    data={[
                                        { label: 'Default', value: 'DEFAULT' },
                                        { label: 'By rank', value: 'RANK' },
                                        { label: 'By largest deficit', value: 'LARGEST_DEFICIT' },
                                    ]}
                                />
                            </div>
                        </Group>
                        <ActionIcon variant="subtle" color="red" onClick={() => removeNode(node)} title="Remove node">
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Group>

                    <Stack gap={6} mt="sm">
                        {node.members.length === 0 && <Text size="xs" c="dimmed">No warehouses yet.</Text>}
                        {node.members.map((m, i) => (
                            <Group key={m.warehouseId} gap="xs" wrap="nowrap" className={styles.memberRow}>
                                <Badge variant="light" color="gray" size="sm" className={styles.rank}>{i + 1}</Badge>
                                <Text size="sm" fw={500} className={styles.memberName}>
                                    {m.warehouseName ?? m.warehouseId}
                                    {m.warehouseName && m.warehouseName !== m.warehouseId && (
                                        <Text span size="xs" c="dimmed"> ({m.warehouseId})</Text>
                                    )}
                                </Text>
                                <Tooltip label="Days of cover this warehouse keeps for itself when it sends. Blank = its own target DoH.">
                                    <NumberInput
                                        size="xs"
                                        w={130}
                                        placeholder="keep floor"
                                        min={0}
                                        suffix="d floor"
                                        value={m.keepFloorDoh}
                                        onChange={v => update(node.key, {
                                            members: node.members.map(x => x.warehouseId === m.warehouseId ? { ...x, keepFloorDoh: v === '' ? '' : Number(v) } : x),
                                        })}
                                    />
                                </Tooltip>
                                <ActionIcon variant="subtle" size="sm" disabled={i === 0} onClick={() => moveMember(node.key, i, -1)} title="Higher priority"><IconArrowUp size={14} /></ActionIcon>
                                <ActionIcon variant="subtle" size="sm" disabled={i === node.members.length - 1} onClick={() => moveMember(node.key, i, 1)} title="Lower priority"><IconArrowDown size={14} /></ActionIcon>
                                <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    color="gray"
                                    onClick={() => update(node.key, { members: node.members.filter(x => x.warehouseId !== m.warehouseId) })}
                                    title="Remove from node"
                                >
                                    <IconX size={14} />
                                </ActionIcon>
                            </Group>
                        ))}
                        {pickerFor === node.key ? (
                            <Select
                                size="xs"
                                w={280}
                                searchable
                                autoFocus
                                placeholder="Pick a warehouse"
                                data={unassigned.map(w => ({ value: w.warehouseId, label: w.name === w.warehouseId ? w.name : `${w.name} (${w.warehouseId})` }))}
                                nothingFoundMessage="Every internal warehouse is already in a node"
                                onChange={v => v && addMember(node.key, v)}
                                onBlur={() => setPickerFor(null)}
                            />
                        ) : (
                            <Button size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />} w="fit-content" onClick={() => setPickerFor(node.key)}>
                                Add warehouse
                            </Button>
                        )}
                    </Stack>
                </div>
            ))}

            {unassigned.length > 0 && (
                <div>
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={6}>Not in any node</Text>
                    <Group gap={6}>
                        {unassigned.map(w => (
                            <Badge key={w.warehouseId} variant="outline" color="gray" size="sm">{w.name}</Badge>
                        ))}
                    </Group>
                    <Text size="xs" c="dimmed" mt={4}>These behave as their own node in a network run.</Text>
                </div>
            )}
        </Stack>
    );
};

export default RebalanceNodesTab;
